import { Head, router } from '@inertiajs/react'
import { Badge, Box, Button, Card, DataList, Flex, Heading, Link, Separator, Text } from '@radix-ui/themes'
import { badgeColors } from '@/shared/constants/constants'
import { ApplicationsApproveProps } from '@/types/applications_approve'

export default function ApplicationsApprove({
  status,
  event_name,
  event_date,
  track_name,
  participant,
  media_group,
  credential,
  approve_path,
  review_path,
}: ApplicationsApproveProps) {
  const details: [string, string][] = [
    ['Email', participant.email],
    ['Primary outlet', media_group.primary_outlet],
    ['Portfolio', media_group.portfolio_link],
    ['Location', participant.location],
    ['Instagram', participant.instagram],
    ['YouTube', participant.youtube],
    ['Flickr', participant.flickr],
    ['Facebook', participant.facebook],
    ['TikTok', participant.tiktok],
    ['Threads', participant.threads],
  ]

  return (
    <>
      <Head title="Approve application" />
      <Box style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        <Heading size="7">Media application</Heading>
        <Text size="3" color="gray" as="p">
          {event_name}
          {event_date ? ` — ${event_date}` : ''} at {track_name}
        </Text>
        <Separator my="4" size="4" />

        <Card>
          <Flex justify="between" align="start" gap="4" mb="3">
            <Heading size="5">{participant.name}</Heading>
            <Badge color={badgeColors[status]} size="2">
              {status}
            </Badge>
          </Flex>
          <DataList.Root>
            {details
              .filter(([, value]) => value !== '')
              .map(([label, value]) => (
                <DataList.Item key={label}>
                  <DataList.Label>{label}</DataList.Label>
                  <DataList.Value>{value}</DataList.Value>
                </DataList.Item>
              ))}
          </DataList.Root>
        </Card>

        {credential && (
          <Card mt="4">
            <Flex justify="between" align="start" gap="4" mb="2">
              <Heading size="5">Annual credential</Heading>
              <Flex gap="2" align="center">
                <Badge color="gray" size="2" style={{ textTransform: 'capitalize' }}>
                  {credential.category}
                </Badge>
                <Badge color={badgeColors[credential.status]} size="2">
                  {credential.status}
                </Badge>
                {credential.expired && (
                  <Badge color="crimson" size="2">
                    Expired
                  </Badge>
                )}
              </Flex>
            </Flex>

            <Text as="p" size="2" color="gray" mb="3">
              {credential.status === 'pending'
                ? "They applied with this still under review. Approving the application here won't approve the credential."
                : `Approved${credential.expires_at ? ` — expires ${credential.expires_at}` : ''}.`}
            </Text>

            <Link href={credential.approve_path} underline="always" size="2">
              Review the credential
            </Link>
          </Card>
        )}

        <Flex direction="column" gap="3" mt="4">
          {status === 'pending' ? (
            <Button size="3" onClick={() => router.post(approve_path)}>
              Approve application
            </Button>
          ) : (
            <Text color="gray">
              This application is already <strong>{status}</strong> — no further action needed here.
            </Text>
          )}
          <Text size="2" color="gray">
            Want the full picture?{' '}
            <Link href={review_path} underline="always">
              See all applications in your dashboard
            </Link>{' '}
            (login required).
          </Text>
        </Flex>
      </Box>
    </>
  )
}
